import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
} from "recharts";
import {
  Eye,
  Film,
  DollarSign,
  TrendingUp,
  ExternalLink,
  Crown,
  AlertTriangle,
  AlertCircle,
  MessageSquare,
  CheckCircle2,
  Clock,
  CalendarOff,
  XCircle,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ActivityFeed } from "@/components/ActivityFeed";
import { Link } from "react-router-dom";
import { fmtCompact, fmtFull, fmtMoney, fmtStatus, daysUntil } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { AppealDialog } from "@/components/clip/AppealDialog";
import { AppealThreadDialog } from "@/components/clip/AppealThreadDialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["all", "pending", "accepted", "flagged", "paid"];
const PLATFORMS = ["all", "tiktok", "instagram", "youtube", "twitch"];

function MetricSkeleton() {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/80 px-5 py-5">
      <Skeleton className="h-3 w-24 rounded" />
      <Skeleton className="mt-3 h-7 w-16 rounded" />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    accepted: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    flagged: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    rejected: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    paid: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
  };
  return (
    <Badge
      data-testid={`submission-status-badge-${status}`}
      className={`${map[status] || "bg-muted text-foreground"} hover:${map[status] || "bg-muted"}`}
    >
      {fmtStatus(status)}
    </Badge>
  );
}

function CampaignStatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    paused: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    ended: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-300",
  };
  return (
    <span
      data-testid={`campaign-status-${status}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[status] || "bg-muted text-foreground"}`}
    >
      {fmtStatus(status || "active")}
    </span>
  );
}

const PIE_COLORS = ["#8B5CF6", "#22D3EE", "#F472B6", "#34D399", "#FBBF24", "#EF4444"];

function PerformanceCard({ title, value, icon: Icon, blob, testId }) {
  // Radial gradient blob positioned on the right/center of the card,
  // matching the mockup style (subtle color glow).
  const blobStyles = {
    blue: {
      background: "radial-gradient(120% 90% at 65% 55%, rgba(90,60,240,0.55) 0%, rgba(90,60,240,0.20) 35%, rgba(0,0,0,0) 70%)",
      iconBg: "bg-white/[0.04] text-white/90",
    },
    green: {
      background: "radial-gradient(120% 90% at 65% 55%, rgba(200,255,0,0.45) 0%, rgba(180,220,20,0.18) 35%, rgba(0,0,0,0) 70%)",
      iconBg: "bg-white/[0.04] text-white/90",
    },
    teal: {
      background: "radial-gradient(120% 90% at 65% 55%, rgba(30,220,180,0.45) 0%, rgba(30,220,180,0.16) 35%, rgba(0,0,0,0) 70%)",
      iconBg: "bg-white/[0.04] text-white/90",
    },
    pink: {
      background: "radial-gradient(120% 90% at 65% 55%, rgba(240,80,180,0.45) 0%, rgba(240,80,180,0.18) 35%, rgba(0,0,0,0) 70%)",
      iconBg: "bg-white/[0.04] text-white/90",
    },
  };
  const s = blobStyles[blob] || blobStyles.blue;
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#111] p-5"
    >
      <div className="pointer-events-none absolute inset-0" style={{ background: s.background }} />
      <div className="relative flex items-center gap-2.5">
        {Icon && (
          <span className={`grid h-7 w-7 place-items-center rounded-[8px] ${s.iconBg}`}>
            <Icon size={15} strokeWidth={2} />
          </span>
        )}
        <div className="text-[13px] font-semibold text-white/90">{title}</div>
      </div>
      <div
        data-testid={`${testId}-value`}
        className="relative mt-3 text-[26px] font-bold tracking-tight text-white sm:text-[28px]"
      >
        {value}
      </div>
    </div>
  );
}

// ---------- NEW: Joined Campaigns & Earnings Card ----------

function UrgencyChip({ endDate, status }) {
  if (status === "ended") {
    return (
      <span data-testid="urgency-ended" className="inline-flex items-center gap-1 rounded-full bg-zinc-500/15 px-2 py-0.5 text-[10px] font-semibold text-zinc-600 dark:text-zinc-300">
        <CalendarOff size={11} /> Ended
      </span>
    );
  }
  const days = daysUntil(endDate);
  if (days == null) return null;
  if (days < 0) {
    return (
      <span data-testid="urgency-overdue" className="inline-flex items-center gap-1 rounded-full bg-zinc-500/15 px-2 py-0.5 text-[10px] font-semibold text-zinc-600 dark:text-zinc-300">
        <CalendarOff size={11} /> Ended
      </span>
    );
  }
  if (days === 0) {
    return (
      <span data-testid="urgency-today" className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:text-rose-300">
        <AlertTriangle size={11} /> Ends today
      </span>
    );
  }
  if (days <= 3) {
    return (
      <span data-testid="urgency-critical" className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:text-rose-300">
        <AlertTriangle size={11} /> Ends in {days}d
      </span>
    );
  }
  if (days <= 7) {
    return (
      <span data-testid="urgency-warning" className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
        <Clock size={11} /> Ends in {days}d
      </span>
    );
  }
  return (
    <span data-testid="urgency-ok" className="inline-flex items-center gap-1 rounded-full bg-foreground/5 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      <Clock size={11} /> {days}d left
    </span>
  );
}

function EligibilityChip({ acceptedViews, minViews }) {
  const needed = Math.max(0, Number(minViews || 0) - Number(acceptedViews || 0));
  if (needed === 0 && Number(minViews || 0) > 0) {
    return (
      <span data-testid="eligibility-eligible" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 size={11} /> Payout Eligible
      </span>
    );
  }
  if (!minViews) {
    return (
      <span data-testid="eligibility-no-min" className="inline-flex items-center gap-1 rounded-full bg-foreground/5 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
        No min views
      </span>
    );
  }
  return (
    <span data-testid="eligibility-not-yet" className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
      Need {fmtCompact(needed)} more views
    </span>
  );
}

function JoinedCampaignsCard({ joined, perCampaignMap }) {
  if (!joined || joined.length === 0) {
    return (
      <Card data-testid="joined-campaigns-card" className="border-border/60 bg-card/80">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Joined Campaigns &amp; Earnings</CardTitle>
        </CardHeader>
        <CardContent>
          <div data-testid="joined-campaigns-empty" className="rounded-xl border border-dashed border-border/60 px-4 py-10 text-center text-sm text-muted-foreground">
            You haven&apos;t joined any campaigns yet. <Link to="/campaigns" className="font-semibold text-[#c8ff00] hover:underline">Browse campaigns</Link>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="joined-campaigns-card" className="border-border/60 bg-card/80">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold">Joined Campaigns &amp; Earnings</CardTitle>
        <span data-testid="joined-campaigns-count" className="text-[11px] font-medium text-muted-foreground">
          {joined.length} {joined.length === 1 ? "campaign" : "campaigns"}
        </span>
      </CardHeader>
      <CardContent className="space-y-2">
        {joined.map((c) => {
          const stats = perCampaignMap[c.id] || { clips: 0, views: 0, revenue: 0 };
          const cpm = Number(c.payout_cpm || 0);
          const maxPer = Number(c.max_payout_per_video || 0);
          const minViews = Number(c.min_views || 0);
          const eligible = stats.views >= minViews && minViews > 0;
          return (
            <Link
              to={`/campaigns/${c.id}`}
              key={c.id}
              data-testid={`joined-campaign-${c.id}`}
              className="block rounded-2xl border border-border/40 bg-background p-3 transition-colors hover:border-[#c8ff00]/40 hover:bg-foreground/[0.02]"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span data-testid={`joined-campaign-name-${c.id}`} className="truncate text-sm font-semibold text-foreground">{c.name}</span>
                    <CampaignStatusBadge status={c.status} />
                    <UrgencyChip endDate={c.end_date} status={c.status} />
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    ${cpm.toFixed(2)} / 1K · Max ${maxPer.toFixed(0)} / video · Min {fmtCompact(minViews)} views
                  </div>
                </div>
                <EligibilityChip acceptedViews={stats.views} minViews={minViews} />
              </div>

              {/* Stats row */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg bg-foreground/[0.03] px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Clips</div>
                  <div data-testid={`joined-clips-${c.id}`} className="mt-0.5 font-bold tabular-nums text-foreground">{stats.clips}</div>
                </div>
                <div className="rounded-lg bg-foreground/[0.03] px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Views</div>
                  <div data-testid={`joined-views-${c.id}`} className="mt-0.5 font-bold tabular-nums text-foreground">{fmtCompact(stats.views)}</div>
                </div>
                <div className="rounded-lg bg-emerald-500/[0.06] px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700/80 dark:text-emerald-300/80">Earned</div>
                  <div data-testid={`joined-earned-${c.id}`} className="mt-0.5 font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{fmtMoney(stats.revenue)}</div>
                </div>
              </div>

              {/* Progress to eligibility */}
              {minViews > 0 && !eligible && c.status === "active" && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Progress to payout</span>
                    <span data-testid={`joined-progress-${c.id}`} className="tabular-nums">{fmtCompact(stats.views)} / {fmtCompact(minViews)}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-foreground/[0.06]">
                    <div
                      className="h-full bg-[#c8ff00] transition-all"
                      style={{ width: `${Math.min(100, (stats.views / Math.max(1, minViews)) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ---------- NEW: All Clips Overview Card ----------

function PlatformIcon({ platform, size = 18 }) {
  const p = String(platform || "").toLowerCase();
  if (p === "instagram") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="url(#allclips-ig)" strokeWidth="2">
        <defs>
          <linearGradient id="allclips-ig" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f9ce34" />
            <stop offset="50%" stopColor="#ee2a7b" />
            <stop offset="100%" stopColor="#6228d7" />
          </linearGradient>
        </defs>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1.2" fill="url(#allclips-ig)" />
      </svg>
    );
  }
  if (p === "tiktok") {
    return (
      <svg width={size - 2} height={size} viewBox="0 0 24 24" fill="white">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.72a8.19 8.19 0 004.76 1.52V6.79a4.83 4.83 0 01-1-.1z" />
      </svg>
    );
  }
  if (p === "youtube") {
    return (
      <svg width={size + 2} height={size - 2} viewBox="0 0 24 24" fill="#FF0000">
        <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2a31.4 31.4 0 000 11.6 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1 31.4 31.4 0 000-11.6zM9.75 15.5v-7l6.25 3.5-6.25 3.5z" />
      </svg>
    );
  }
  return <Film size={size} className="text-zinc-400" />;
}

function StatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  if (s === "accepted" || s === "approved" || s === "paid") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11.5px] font-bold text-emerald-400">
        <CheckCircle2 size={13} strokeWidth={2.5} />
        <span>Approved</span>
      </span>
    );
  }
  if (s === "pending") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-1 text-[11.5px] font-bold text-amber-400">
        <Clock size={13} strokeWidth={2.5} />
        <span>Pending</span>
      </span>
    );
  }
  if (s === "flagged" || s === "rejected") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-2.5 py-1 text-[11.5px] font-bold text-rose-400">
        <XCircle size={13} strokeWidth={2.5} />
        <span>Rejected</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-white/[0.06] px-2.5 py-1 text-[11.5px] font-bold text-zinc-300">
      {fmtStatus(s)}
    </span>
  );
}

function relativeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(diffMs / day);
  if (days <= 0) {
    const hrs = Math.floor(diffMs / (60 * 60 * 1000));
    if (hrs <= 0) return "Just now";
    return `${hrs} ${hrs === 1 ? "Hour" : "Hours"} Ago`;
  }
  if (days === 1) return "1 Day Ago";
  if (days < 30) return `${days} Days Ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "1 Month Ago";
  if (months < 12) return `${months} Months Ago`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? "Year" : "Years"} Ago`;
}

function AllClipsOverviewCard({ submissions, platformFilter, onPlatformFilter, statusFilter, onStatusFilter, accounts, accountFilter, onAccountFilter, campaignFilter, onCampaignFilter, campaigns, appealsBySubId = {}, onAppealChange }) {
  const [appealOpen, setAppealOpen] = React.useState(null);     // submission for new appeal dialog
  const [threadOpen, setThreadOpen] = React.useState(null);     // { submission, appeal } for thread dialog
  const [notesOpen, setNotesOpen] = React.useState(null);       // submission for view notes dialog
  const [search, setSearch] = React.useState("");
  const [pageSize, setPageSize] = React.useState(4);
  const [page, setPage] = React.useState(1);

  // Search filter (client-side over campaign name + handle + platform)
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter((s) => {
      const hay = [s.campaign_name, s.account_handle, s.handle, s.platform, s.title]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [submissions, search]);

  const sorted = React.useMemo(
    () => [...filtered].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [filtered]
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  React.useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);
  const pageItems = sorted.slice((page - 1) * pageSize, page * pageSize);

  function notesCount(s) {
    const appeal = appealsBySubId[s.id];
    let c = 0;
    if (s.rejection_reason || s.status_reason) c += 1;
    if (appeal && Array.isArray(appeal.messages)) c += appeal.messages.length;
    else if (appeal) c += 1;
    return c;
  }

  return (
    <Card data-testid="all-clips-overview-card" className="border-white/[0.06] bg-[#111]">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <CardTitle className="flex items-center gap-2 text-[14px] font-semibold text-white">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-white/[0.05]">
              <Film size={14} className="text-white" />
            </span>
            All Clips
          </CardTitle>

          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="clips-search-input"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search Clips"
                className="h-8 w-[140px] rounded-full border border-white/[0.08] bg-black/40 pl-7 pr-3 text-[12px] text-white placeholder:text-zinc-500 focus:border-[#c8ff00]/40 focus:outline-none"
              />
            </div>

            {/* Campaign filter */}
            <Select value={campaignFilter} onValueChange={(v) => { onCampaignFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="clips-campaign-filter" className="h-8 w-[135px] rounded-full border-white/[0.08] bg-black/40 text-[12px] text-white">
                <SelectValue placeholder="All Campaigns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="clips-campaign-all">All Campaigns</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`clips-campaign-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Platform filter */}
            <Select value={platformFilter} onValueChange={(v) => { onPlatformFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="clips-platform-filter" className="h-8 w-[125px] rounded-full border-white/[0.08] bg-black/40 text-[12px] text-white">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`clips-platform-${p}`}>{p === "all" ? "All Platforms" : fmtStatus(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status filter */}
            <Select value={statusFilter} onValueChange={(v) => { onStatusFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="clips-status-filter" className="h-8 w-[120px] rounded-full border-white/[0.08] bg-black/40 text-[12px] text-white">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`clips-status-${s}`}>{s === "all" ? "All Statuses" : fmtStatus(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Account filter */}
            <Select value={accountFilter} onValueChange={(v) => { onAccountFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="clips-account-filter" className="h-8 w-[125px] rounded-full border-white/[0.08] bg-black/40 text-[12px] text-white">
                <SelectValue placeholder="All Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="clips-account-all">All Accounts</SelectItem>
                {(accounts || []).map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`clips-account-${a.id}`}>{a.handle || `@${(a.handle || "").replace(/^@/, "")}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Per-page */}
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger data-testid="clips-page-size" className="h-8 w-[70px] rounded-full border-white/[0.08] bg-black/40 text-[12px] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[4, 8, 12, 20].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Pagination */}
            <div data-testid="clips-pagination" className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-black/40 px-2 py-1 text-[11.5px] text-zinc-300">
              <button
                data-testid="clips-page-prev"
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="grid h-5 w-5 place-items-center rounded-full text-zinc-400 hover:text-white disabled:opacity-30"
              >
                <ChevronLeft size={13} />
              </button>
              <span data-testid="clips-page-indicator" className="tabular-nums font-semibold">{page} of {totalPages}</span>
              <button
                data-testid="clips-page-next"
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="grid h-5 w-5 place-items-center rounded-full text-zinc-400 hover:text-white disabled:opacity-30"
              >
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {sorted.length === 0 ? (
          <div data-testid="clips-overview-empty" className="rounded-xl border border-dashed border-white/[0.08] px-4 py-10 text-center text-sm text-zinc-500">
            No clips match the current filters. Try changing them or <Link to="/campaigns" className="font-semibold text-[#c8ff00] hover:underline">submit a clip</Link>.
          </div>
        ) : (
          <div data-testid="clips-table" className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-separate border-spacing-y-2 text-left">
              <thead>
                <tr className="text-[11.5px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 pb-2 font-semibold">Post</th>
                  <th className="px-3 pb-2 font-semibold">Campaign</th>
                  <th className="px-3 pb-2 font-semibold">Views</th>
                  <th className="px-3 pb-2 font-semibold">Status</th>
                  <th className="px-3 pb-2 font-semibold">Earnings</th>
                  <th className="px-3 pb-2 font-semibold">Submitted</th>
                  <th className="px-3 pb-2 font-semibold">Platform Notes</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((s) => {
                  const isRejected = s.status === "flagged" || s.status === "rejected";
                  const appeal = appealsBySubId[s.id];
                  const nCount = notesCount(s);
                  return (
                    <tr
                      key={s.id}
                      data-testid={`clip-row-${s.id}`}
                      className="rounded-xl border-white/[0.05] bg-white/[0.02] text-[13px] text-zinc-200 transition-colors hover:bg-white/[0.04]"
                    >
                      {/* Post */}
                      <td className="rounded-l-xl px-3 py-3">
                        <div className="flex items-center gap-2">
                          <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/[0.04]">
                            <PlatformIcon platform={s.platform} size={16} />
                          </span>
                          <span data-testid={`clip-handle-${s.id}`} className="truncate font-semibold text-white">
                            {s.account_handle || s.handle || "—"}
                          </span>
                          {s.video_url && (
                            <a
                              data-testid={`clip-link-${s.id}`}
                              href={s.video_url}
                              target="_blank"
                              rel="noreferrer"
                              className="grid h-6 w-6 place-items-center rounded-md text-zinc-500 hover:bg-white/[0.06] hover:text-white"
                              title="Open post"
                            >
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      </td>

                      {/* Campaign */}
                      <td className="px-3 py-3">
                        <div data-testid={`clip-campaign-${s.id}`} className="truncate font-semibold text-white">
                          {s.campaign_name || "—"}
                        </div>
                      </td>

                      {/* Views */}
                      <td className="px-3 py-3">
                        <div data-testid={`clip-views-${s.id}`} className="tabular-nums font-semibold text-white">
                          {fmtCompact(s.views || 0)}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <StatusPill status={s.status} />
                          {isRejected && !appeal && (
                            <button
                              data-testid={`clip-appeal-btn-${s.id}`}
                              type="button"
                              onClick={() => setAppealOpen(s)}
                              className="text-[11.5px] font-semibold text-[#c8ff00] underline underline-offset-2 hover:text-[#b8ef00]"
                            >
                              Appeal
                            </button>
                          )}
                          {isRejected && appeal && (
                            <button
                              data-testid={`clip-view-appeal-btn-${s.id}`}
                              type="button"
                              onClick={() => setThreadOpen({ submission: s, appeal })}
                              className="text-[11.5px] font-semibold text-[#c8ff00] underline underline-offset-2 hover:text-[#b8ef00]"
                            >
                              Appeal · {appeal.status || "pending"}
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Earnings */}
                      <td className="px-3 py-3">
                        <div data-testid={`clip-earned-${s.id}`} className={`tabular-nums font-semibold ${Number(s.earned || 0) > 0 ? "text-emerald-300" : "text-zinc-500"}`}>
                          {Number(s.earned || 0) > 0 ? fmtMoney(s.earned) : "—"}
                        </div>
                      </td>

                      {/* Submitted */}
                      <td className="px-3 py-3 text-zinc-400">
                        {relativeAgo(s.created_at)}
                      </td>

                      {/* Platform Notes */}
                      <td className="rounded-r-xl px-3 py-3">
                        <button
                          type="button"
                          data-testid={`clip-notes-view-${s.id}`}
                          onClick={() => setNotesOpen(s)}
                          disabled={nCount === 0}
                          className="relative inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white underline underline-offset-2 hover:text-[#c8ff00] disabled:cursor-not-allowed disabled:text-zinc-600 disabled:no-underline"
                        >
                          View
                          {nCount > 0 && (
                            <span
                              data-testid={`clip-notes-badge-${s.id}`}
                              className="grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-[9px] font-bold text-white shadow-[0_0_10px_rgba(52,211,153,0.5)]"
                            >
                              {nCount}
                            </span>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Appeal submit dialog */}
      <AppealDialog
        open={!!appealOpen}
        onOpenChange={(o) => { if (!o) setAppealOpen(null); }}
        submission={appealOpen}
        onSubmitted={() => { setAppealOpen(null); onAppealChange?.(); }}
      />

      {/* Appeal thread (read-only for clipper) */}
      <AppealThreadDialog
        open={!!threadOpen}
        onOpenChange={(o) => { if (!o) setThreadOpen(null); }}
        submission={threadOpen?.submission}
        appeal={threadOpen?.appeal}
        adminMode={false}
      />

      {/* Notes dialog — shows rejection reason + appeal messages */}
      <Dialog open={!!notesOpen} onOpenChange={(o) => { if (!o) setNotesOpen(null); }}>
        <DialogContent data-testid="clip-notes-dialog" className="max-w-md border-white/[0.08] bg-[#0f0f0f] text-white">
          <DialogHeader>
            <DialogTitle>Platform Notes</DialogTitle>
            <DialogDescription className="text-zinc-500">Feedback and updates on this clip.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {notesOpen?.rejection_reason || notesOpen?.status_reason ? (
              <div className="rounded-lg border border-rose-500/25 bg-rose-500/10 p-3 text-[13px]">
                <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-400">
                  <AlertCircle size={11} /> Moderator note
                </div>
                <div className="whitespace-pre-wrap text-zinc-200">
                  {notesOpen?.rejection_reason || notesOpen?.status_reason}
                </div>
              </div>
            ) : null}
            {(() => {
              const appeal = notesOpen ? appealsBySubId[notesOpen.id] : null;
              if (!appeal) return null;
              const msgs = Array.isArray(appeal.messages) ? appeal.messages : [];
              return (
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#c8ff00]">
                    <MessageSquare size={11} /> Appeal · {appeal.status || "pending"}
                  </div>
                  {msgs.length === 0 ? (
                    <div className="text-[12px] text-zinc-500">No messages on this appeal yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {msgs.map((m, i) => (
                        <div key={i} className="rounded-md bg-black/40 px-2 py-1.5 text-[12px] text-zinc-300">
                          <span className="mr-2 text-[10px] font-bold uppercase text-zinc-500">{m.author_role || "user"}</span>
                          {m.text || m.message || ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function DashboardPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [joinedCampaigns, setJoinedCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState(null);
  const [appeals, setAppeals] = useState([]);
  const [error, setError] = useState(null);

  const [campaignId, setCampaignId] = useState("all");
  const [accountId, setAccountId] = useState("all");
  const [status, setStatus] = useState("all");
  const [platform, setPlatform] = useState("all");

  const params = useMemo(() => {
    const p = {};
    if (campaignId !== "all") p.campaign_id = campaignId;
    if (accountId !== "all") p.account_id = accountId;
    if (status !== "all") p.status = status;
    return p;
  }, [campaignId, accountId, status]);

  async function load() {
    setError(null);
    try {
      const [cRes, jRes, aRes, dRes, apRes] = await Promise.all([
        api.get("/campaigns"),
        api.get("/campaigns/joined").catch(() => ({ data: [] })),
        api.get("/accounts"),
        api.get("/stats/dashboard", { params }),
        api.get("/appeals/me").catch(() => ({ data: [] })),
      ]);
      setCampaigns(cRes.data || []);
      setJoinedCampaigns(jRes.data || []);
      setAccounts(aRes.data || []);
      setData(dRes.data);
      setAppeals(Array.isArray(apRes.data) ? apRes.data : []);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, accountId, status]);

  const summary = data?.summary || {};
  const perCampaign = data?.per_campaign || [];
  const perAccount = data?.per_account || [];
  const perPlatform = data?.per_platform || [];
  const submissions = data?.submissions || [];

  // Filter by platform locally (server doesn't filter by platform yet)
  const filteredSubmissions = useMemo(() => {
    if (platform === "all") return submissions;
    return submissions.filter((s) => String(s.platform || "").toLowerCase() === platform);
  }, [submissions, platform]);

  const perCampaignMap = useMemo(() => {
    const m = {};
    for (const pc of perCampaign) m[pc.campaign_id] = pc;
    return m;
  }, [perCampaign]);

  // Map every submission to its most recent appeal so we can render the
  // appeal/View Appeal action on rejected clip cards.
  const appealsBySubId = useMemo(() => {
    const m = {};
    for (const a of appeals) {
      const sid = a.submission_id;
      if (!sid) continue;
      const prev = m[sid];
      if (!prev || (a.created_at && a.created_at > prev.created_at)) m[sid] = a;
    }
    return m;
  }, [appeals]);

  const pieAccounts = perAccount.map((a) => ({
    name: `${a.platform || ""} ${a.handle || a.account_id.slice(0, 6)}`.trim(),
    value: a.views,
  }));

  const piePlatforms = perPlatform.map((p) => ({
    name: fmtStatus(p.platform),
    value: p.views,
  }));

  const topClips = useMemo(() => {
    return [...submissions].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5);
  }, [submissions]);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 data-testid="dashboard-title" className="page-title">
            Dashboard
          </h1>
          <p data-testid="dashboard-subtitle" className="page-subtitle">
            Track views, revenue, and submission status across your campaigns.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger data-testid="dashboard-account-filter" className="h-9 w-[180px] rounded-full border-border/60 bg-background text-sm">
              <SelectValue placeholder="Account" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem data-testid="dashboard-account-all" value="all">All accounts</SelectItem>
              {accounts.map((a) => (
                <SelectItem data-testid={`dashboard-account-${a.id}`} key={a.id} value={a.id}>{a.platform} · {a.handle}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div data-testid="dashboard-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Performance Overview */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {!data ? (
          <><MetricSkeleton /><MetricSkeleton /><MetricSkeleton /><MetricSkeleton /></>
        ) : (
          <>
            <PerformanceCard testId="metric-total-views" title="Views" value={summary.total_views != null ? fmtCompact(summary.total_views) : "—"} icon={Eye} blob="blue" />
            <PerformanceCard testId="metric-total-revenue" title="Payouts" value={summary.total_revenue != null ? fmtMoney(summary.total_revenue) : "—"} icon={DollarSign} blob="green" />
            <PerformanceCard testId="metric-total-videos" title="Total Clips" value={summary.total_videos ?? "—"} icon={Film} blob="teal" />
            <PerformanceCard testId="metric-avg-engagement" title="Avg. Engagement" value={summary.avg_engagement != null ? `${Number(summary.avg_engagement).toFixed(2)}%` : "—"} icon={TrendingUp} blob="pink" />
          </>
        )}
      </div>

      {/* Overview (Joined Campaigns & Earnings) | Clips (All Clips Overview) tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList data-testid="dashboard-section-tabs" className="grid w-full max-w-xs grid-cols-2 rounded-2xl border border-[#1a1a1a] bg-black/60 p-1">
          <TabsTrigger value="overview" data-testid="dashboard-tab-overview" className="rounded-xl text-xs font-semibold data-[state=active]:bg-[#c8ff00] data-[state=active]:text-black">Overview</TabsTrigger>
          <TabsTrigger value="clips" data-testid="dashboard-tab-clips" className="rounded-xl text-xs font-semibold data-[state=active]:bg-[#c8ff00] data-[state=active]:text-black">Clips</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-3">
          <JoinedCampaignsCard joined={joinedCampaigns} perCampaignMap={perCampaignMap} />
        </TabsContent>

        <TabsContent value="clips" className="mt-3">
          <AllClipsOverviewCard
            submissions={filteredSubmissions}
            platformFilter={platform}
            onPlatformFilter={setPlatform}
            statusFilter={status}
            onStatusFilter={setStatus}
            campaignFilter={campaignId}
            onCampaignFilter={setCampaignId}
            accountFilter={accountId}
            onAccountFilter={setAccountId}
            accounts={accounts}
            campaigns={campaigns}
            appealsBySubId={appealsBySubId}
            onAppealChange={() => api.get("/appeals/me").catch(() => ({ data: [] })).then((r) => setAppeals(Array.isArray(r.data) ? r.data : []))}
          />
        </TabsContent>
      </Tabs>

      {/* Top Performing Clips — 2-up grid */}
      {data && topClips.length > 0 && (
        <Card data-testid="top-posts-section" className="border-border/60 bg-card/80">
          <CardHeader className="pb-3">
            <CardTitle data-testid="top-posts-title" className="flex items-center gap-2 text-sm font-semibold">
              <Crown size={16} className="text-amber-500" />
              Top Performing Clips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {topClips.slice(0, 2).map((clip) => (
                <a
                  key={clip.id}
                  data-testid={`top-clip-${clip.id}`}
                  href={clip.video_url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-black/40 p-3 transition-colors hover:border-[#c8ff00]/30 hover:bg-black/60"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/[0.04] text-white">
                    {(() => {
                      const platform = String(clip.platform || "").toLowerCase();
                      if (platform === "instagram") {
                        return (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="url(#ig-grad)" strokeWidth="2">
                            <defs>
                              <linearGradient id="ig-grad" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#f9ce34" />
                                <stop offset="50%" stopColor="#ee2a7b" />
                                <stop offset="100%" stopColor="#6228d7" />
                              </linearGradient>
                            </defs>
                            <rect x="3" y="3" width="18" height="18" rx="5" />
                            <circle cx="12" cy="12" r="4" />
                            <circle cx="17.5" cy="6.5" r="1.2" fill="url(#ig-grad)" />
                          </svg>
                        );
                      }
                      if (platform === "tiktok") {
                        return (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.72a8.19 8.19 0 004.76 1.52V6.79a4.83 4.83 0 01-1-.1z" />
                          </svg>
                        );
                      }
                      if (platform === "youtube") {
                        return (
                          <svg width="20" height="16" viewBox="0 0 24 24" fill="#FF0000">
                            <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2a31.4 31.4 0 000 11.6 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1 31.4 31.4 0 000-11.6zM9.75 15.5v-7l6.25 3.5-6.25 3.5z" />
                          </svg>
                        );
                      }
                      return <Crown size={16} className="text-amber-500" />;
                    })()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div
                      data-testid={`top-clip-handle-${clip.id}`}
                      className="truncate text-[13.5px] font-semibold text-white"
                    >
                      {clip.account_handle || clip.handle || clip.title || fmtStatus(clip.platform || "clip")}
                    </div>
                    <div
                      data-testid={`top-clip-views-${clip.id}`}
                      className="mt-0.5 text-[11.5px] font-medium text-zinc-400 tabular-nums"
                    >
                      {fmtCompact(clip.views || 0)} Views
                    </div>
                  </div>
                  <StatusBadge status={clip.status} />
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/[0.04] text-zinc-400 transition-colors group-hover:bg-[#c8ff00]/12 group-hover:text-[#c8ff00]">
                    <ExternalLink size={13} />
                  </span>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="chart-views-by-account" className="border-border/60 bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle data-testid="chart-views-by-account-title" className="text-sm font-semibold">Views by Account</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            {pieAccounts.length ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <PieChart>
                  <Pie data={pieAccounts} dataKey="value" nameKey="name" innerRadius={65} outerRadius={105} paddingAngle={4} strokeWidth={0}>
                    {pieAccounts.map((_, idx) => (<Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))" }} />))}
                  </Pie>
                  <Tooltip formatter={(value) => fmtFull(value)} contentStyle={{ background: "#000000", border: "1px solid #1a1a1a", borderRadius: "12px", fontSize: "12px", color: "#ffffff" }} itemStyle={{ color: "#ffffff" }} labelStyle={{ color: "#ffffff" }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data yet</div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="chart-views-by-platform" className="border-border/60 bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle data-testid="chart-views-by-platform-title" className="text-sm font-semibold">Views by Platform</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            {piePlatforms.length ? (
              <div className="flex h-full items-center gap-4">
                <div className="w-1/2 h-full">
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                    <PieChart>
                      <Pie data={piePlatforms} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={3} strokeWidth={0}>
                        {piePlatforms.map((_, idx) => (<Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />))}
                      </Pie>
                      <Tooltip formatter={(value) => fmtFull(value)} contentStyle={{ background: "#000000", border: "1px solid #1a1a1a", borderRadius: "12px", fontSize: "12px", color: "#ffffff" }} itemStyle={{ color: "#ffffff" }} labelStyle={{ color: "#ffffff" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-2">
                  {piePlatforms.map((p, idx) => {
                    const totalV = piePlatforms.reduce((a, b) => a + b.value, 0);
                    const pct = totalV > 0 ? ((p.value / totalV) * 100).toFixed(1) : 0;
                    return (
                      <div key={p.name} className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                        <span className="text-xs text-muted-foreground flex-1 truncate">{p.name}</span>
                        <span className="text-xs font-medium tabular-nums text-foreground">{fmtCompact(p.value)}</span>
                        <span className="text-[10px] text-muted-foreground tabular-nums">({pct}%)</span>
                      </div>
                    );
                  })}
                  <div className="pt-2 border-t border-border/40">
                    <div className="text-xs text-muted-foreground">Total Views</div>
                    <div className="text-lg font-bold text-foreground">{fmtCompact(piePlatforms.reduce((a, b) => a + b.value, 0))}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Global activity feed — Live clip submissions */}
      <ActivityFeed limit={8} />
    </div>
  );
}
